/**
 * Firebase Phone Auth Provider (Web)
 *
 * Uses Firebase Phone Authentication for SMS verification on web platform.
 * Uses RecaptchaVerifier for bot protection.
 */

import { signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import {
  PhoneAuthProvider,
  PhoneAuthSession,
  PhoneAuthResult,
  PhoneAuthError,
  PhoneAuthErrorCode,
} from '../../../../src/services/phoneAuth/PhoneAuthProvider.interface';

export class FirebasePhoneAuthProviderWeb implements PhoneAuthProvider {
  readonly providerName = 'firebase' as const;
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private recaptchaContainerId: string = 'recaptcha-container';

  /**
   * Set custom reCAPTCHA container ID
   * Default: 'recaptcha-container'
   */
  setRecaptchaContainerId(containerId: string): void {
    this.recaptchaContainerId = containerId;
  }

  /**
   * Initialize Firebase Phone Auth with reCAPTCHA
   * Creates a visible "I'm not a robot" checkbox
   */
  async initialize(): Promise<void> {
    try {
      // Check if container exists
      const container = document.getElementById(this.recaptchaContainerId);
      if (!container) {
        throw new PhoneAuthError(
          PhoneAuthErrorCode.RECAPTCHA_FAILED,
          `reCAPTCHA container with id "${this.recaptchaContainerId}" not found in DOM`
        );
      }

      // Clear any existing verifier
      if (this.recaptchaVerifier) {
        try {
          await this.recaptchaVerifier.clear();
        } catch (e) {
          // Ignore errors when clearing
        }
      }

      // Create reCAPTCHA v2 Invisible verifier (no checkbox!)
      this.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        this.recaptchaContainerId,
        {
          size: 'invisible', // 'invisible' = automatic verification without checkbox
          callback: () => {
            console.log('[FirebaseProviderWeb] reCAPTCHA solved automatically');
          },
          'expired-callback': () => {
            console.warn('[FirebaseProviderWeb] reCAPTCHA expired');
          },
          'error-callback': (error: any) => {
            console.error('[FirebaseProviderWeb] reCAPTCHA error:', error);
          },
        }
      );

      // Render the reCAPTCHA widget
      await this.recaptchaVerifier.render();

      console.log('[FirebaseProviderWeb] Initialized successfully');
    } catch (error: any) {
      console.error('[FirebaseProviderWeb] Initialization error:', error);

      // Re-throw if already PhoneAuthError
      if (error instanceof PhoneAuthError) {
        throw error;
      }

      throw new PhoneAuthError(
        PhoneAuthErrorCode.RECAPTCHA_FAILED,
        `Failed to initialize reCAPTCHA: ${error.message}`,
        error
      );
    }
  }

  /**
   * Send verification code via Firebase Phone Auth
   */
  async sendVerificationCode(phoneNumber: string): Promise<PhoneAuthSession> {
    try {
      if (!this.recaptchaVerifier) {
        throw new PhoneAuthError(
          PhoneAuthErrorCode.RECAPTCHA_FAILED,
          'reCAPTCHA verifier not initialized. Call initialize() first.'
        );
      }

      console.log(
        '[FirebaseProviderWeb] Sending verification code to:',
        phoneNumber
      );

      // Call Firebase Phone Auth
      // User must solve reCAPTCHA before SMS is sent
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        this.recaptchaVerifier
      );

      console.log('[FirebaseProviderWeb] SMS sent successfully via Firebase');

      return {
        sessionId: `firebase-web-${Date.now()}`,
        phoneNumber,
        provider: 'firebase',
        expiresAt: new Date(Date.now() + 5 * 60 * 1000), // 5 minutes
        confirmationResult,
      };
    } catch (error: any) {
      console.error('[FirebaseProviderWeb] Error sending SMS:', error);

      // Reset reCAPTCHA after error
      if (this.recaptchaVerifier) {
        try {
          await this.recaptchaVerifier.clear();
          await this.recaptchaVerifier.render();
        } catch (e) {
          // Ignore errors when resetting
        }
      }

      // Map Firebase errors to PhoneAuthErrorCodes
      if (error.code === 'auth/invalid-phone-number') {
        throw new PhoneAuthError(
          PhoneAuthErrorCode.INVALID_PHONE_NUMBER,
          'Invalid phone number format. Use E.164 format (e.g., +6591234567)',
          error
        );
      }

      if (error.code === 'auth/too-many-requests') {
        throw new PhoneAuthError(
          PhoneAuthErrorCode.TOO_MANY_REQUESTS,
          'Too many SMS requests. Please try again later.',
          error
        );
      }

      if (error.code === 'auth/quota-exceeded') {
        throw new PhoneAuthError(
          PhoneAuthErrorCode.QUOTA_EXCEEDED,
          'SMS quota exceeded. Please try again tomorrow.',
          error
        );
      }

      if (error.code === 'auth/captcha-check-failed') {
        throw new PhoneAuthError(
          PhoneAuthErrorCode.RECAPTCHA_FAILED,
          'reCAPTCHA verification failed. Please try again.',
          error
        );
      }

      // Re-throw if already PhoneAuthError
      if (error instanceof PhoneAuthError) {
        throw error;
      }

      throw new PhoneAuthError(
        PhoneAuthErrorCode.UNKNOWN_ERROR,
        error.message || 'Failed to send SMS verification code',
        error
      );
    }
  }

  /**
   * Verify SMS code via Firebase Phone Auth
   *
   * IMPORTANT: This will create/authenticate a phone-based Firebase user.
   * Similar to mobile, we need to handle potential auth conflicts.
   */
  async verifyCode(
    session: PhoneAuthSession,
    code: string
  ): Promise<PhoneAuthResult> {
    try {
      if (!session.confirmationResult) {
        throw new PhoneAuthError(
          PhoneAuthErrorCode.UNKNOWN_ERROR,
          'Invalid session: missing confirmation result'
        );
      }

      console.log('[FirebaseProviderWeb] Verifying code...');

      // Verify the code
      // WARNING: This will sign out the current email/password user
      // and sign in with the phone credential!
      await session.confirmationResult.confirm(code);

      console.log('[FirebaseProviderWeb] Code verified successfully');

      // At this point, auth.currentUser is now the phone-authenticated user
      // The calling code needs to handle re-authenticating with email/password

      return {
        success: true,
        error: undefined,
      };
    } catch (error: any) {
      console.error('[FirebaseProviderWeb] Error verifying code:', error);

      // Map Firebase errors
      if (error.code === 'auth/invalid-verification-code') {
        return {
          success: false,
          error: 'Invalid verification code',
        };
      }

      if (error.code === 'auth/code-expired') {
        throw new PhoneAuthError(
          PhoneAuthErrorCode.CODE_EXPIRED,
          'Verification code has expired. Please request a new one.',
          error
        );
      }

      if (error.code === 'auth/session-expired') {
        throw new PhoneAuthError(
          PhoneAuthErrorCode.CODE_EXPIRED,
          'Verification session has expired. Please request a new code.',
          error
        );
      }

      // Re-throw if already PhoneAuthError
      if (error instanceof PhoneAuthError) {
        throw error;
      }

      return {
        success: false,
        error: error.message || 'Verification failed',
      };
    }
  }

  /**
   * Clean up resources
   * Removes reCAPTCHA widget from DOM
   */
  async dispose(): Promise<void> {
    if (this.recaptchaVerifier) {
      try {
        await this.recaptchaVerifier.clear();
      } catch (error) {
        console.warn('[FirebaseProviderWeb] Error clearing reCAPTCHA:', error);
      }
      this.recaptchaVerifier = null;
    }
    console.log('[FirebaseProviderWeb] Disposed');
  }

  /**
   * Reset reCAPTCHA widget
   * Useful after errors or when user wants to try again
   */
  async resetRecaptcha(): Promise<void> {
    if (this.recaptchaVerifier) {
      try {
        await this.recaptchaVerifier.clear();
        await this.recaptchaVerifier.render();
        console.log('[FirebaseProviderWeb] reCAPTCHA reset successfully');
      } catch (error) {
        console.error(
          '[FirebaseProviderWeb] Error resetting reCAPTCHA:',
          error
        );
        throw new PhoneAuthError(
          PhoneAuthErrorCode.RECAPTCHA_FAILED,
          'Failed to reset reCAPTCHA',
          error
        );
      }
    }
  }
}
